import {useEffect, useRef} from "react";
import type {
    ChecklistData,
    ChecklistToc,
    CollectionDataResponse,
    Status,
} from "../../types";
import {ChecklistEventTypes} from "./useChecklistState";
import type {ChecklistAction} from "./useChecklistState";
import {getVisibleItemIds} from "../utils";
import {handleCheckItemStatusClick as handleStatusClickLogic} from "../checklistStatusHandler";

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

interface ScrollSpy {
    containerRef: React.RefObject<HTMLDivElement | null>;
    tocContainerRef: React.RefObject<HTMLDivElement | null>;
    locked: React.RefObject<boolean>;
    manuallySetActiveElementId: React.RefObject<string | null>;
    scrollToCheckItem: (id: string) => void;
    scrollToActiveTocItem: (id: string) => void;
    setActiveTocItem: (id: string | null) => void;
}

interface Mutations {
    patchDimensions: (params: {
        topicId: string;
        resourceId: string;
        collectionId: string;
        dimensions: string[];
        labels: {collectionId: string; groupId: string; labelId: string}[];
    }) => Promise<unknown>;
    patchCollectionName: (params: {
        topicId: string;
        resourceId: string;
        collectionId: string;
        name: string;
    }) => Promise<unknown>;
    patchStatusForCheckItems: (params: {
        topicId: string;
        collectionId: string;
        itemIds: string[];
        status: Status;
        resourceId: string;
    }) => void;
    patchDocumentSelected: (params: {
        topicId: string;
        resourceId: string;
        collectionId: string;
        documentId: string;
        selected: boolean;
    }) => void;
}

interface ModalActions {
    openCreateFilling: () => void;
    openConfirmCheckAll: (data: {
        leafNodeIds: string[];
        status: Status;
    }) => void;
    closeModal: () => void;
    confirmCheckAllData: {leafNodeIds: string[]; status: Status} | undefined;
}

interface UseChecklistActionsParams {
    checklistData: ChecklistData;
    collectionData: CollectionDataResponse | undefined;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
    labelData: Record<string, string>;
    hiddenIds: Set<string>;
    allItemsExpanded: boolean;

    state: {
        items: Record<
            string,
            {isLoading: boolean; success: boolean; error: boolean}
        >;
        openItems: Record<string, boolean>;
        filter: {statusFilterSelected: Record<string, (Status | string)[]>};
        searchTerm: string;
        fetchedAll: boolean;
        fetchingAll: boolean;
    };
    dispatch: React.Dispatch<ChecklistAction>;
    fetchItem: (id: string) => void;
    fetchAll: (id: string, tocIds: string[]) => void;

    scrollSpy: ScrollSpy;
    mutations: Mutations;
    modals: ModalActions;
}

// -------------------------------------------------------------------------
// Hook
// -------------------------------------------------------------------------

/**
 * All user-initiated action handlers for the checklist orchestrator.
 *
 * Also contains the selective-expansion effect that auto-opens matching
 * items after a fetch-all completes with active filters/search.
 */
export const useChecklistActions = ({
    checklistData,
    collectionData,
    viewFilteredToc,
    effectiveRootNodeId,
    labelData,
    hiddenIds,
    allItemsExpanded,
    state,
    dispatch,
    fetchItem,
    fetchAll,
    scrollSpy,
    mutations,
    modals,
}: UseChecklistActionsParams) => {
    const pendingSelectiveExpansionRef = useRef(false);
    const rootNodeId = checklistData.rootNodeId ?? "";

    // -----------------------------------------------------------------
    // Selective expansion effect
    // -----------------------------------------------------------------

    useEffect(() => {
        const hasActiveFilter =
            state.searchTerm.length > 0 ||
            Object.keys(state.filter.statusFilterSelected).length > 0;

        if (
            pendingSelectiveExpansionRef.current &&
            state.fetchedAll &&
            !state.fetchingAll &&
            hasActiveFilter &&
            effectiveRootNodeId
        ) {
            pendingSelectiveExpansionRef.current = false;
            const visibleItemIds = getVisibleItemIds(
                viewFilteredToc,
                effectiveRootNodeId,
                hiddenIds
            );
            dispatch({
                type: ChecklistEventTypes.EXPAND_VISIBLE,
                openItems: visibleItemIds,
            });
        }
    }, [
        state.fetchedAll,
        state.fetchingAll,
        state.searchTerm,
        state.filter.statusFilterSelected,
        viewFilteredToc,
        effectiveRootNodeId,
        hiddenIds,
        dispatch,
    ]);

    // -----------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------

    const triggerFetchAll = () => {
        const tocIds = Object.keys(checklistData.toc);
        dispatch({
            type: ChecklistEventTypes.FETCH_ALL,
            id: rootNodeId,
            tocIds,
        });
        fetchAll(rootNodeId, tocIds);
    };

    const patchCheckItems = (itemIds: string[], status: Status) => {
        const topicId = checklistData.metadata.topicId;
        const resourceId = checklistData.metadata.id;
        const cId = collectionData?.metadata?.collectionId;
        if (topicId && cId && resourceId) {
            mutations.patchStatusForCheckItems({
                topicId,
                collectionId: cId,
                itemIds,
                status,
                resourceId,
            });
        }
    };

    // -----------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------

    const handleClickTocItem = (id: string, parentId: string) => {
        scrollSpy.locked.current = true;
        scrollSpy.manuallySetActiveElementId.current = id;
        scrollSpy.setActiveTocItem(id);
        dispatch({
            type: ChecklistEventTypes.CLICK_TOC_ITEM,
            id,
            parent_id: parentId,
        });

        if (!state.items[id]?.success && !state.items[id]?.isLoading) {
            fetchItem(id);
        }

        setTimeout(() => {
            scrollSpy.scrollToActiveTocItem(id);
            scrollSpy.scrollToCheckItem(id);
        }, 0);
    };

    const handleClickChecklistItem = (id: string, parentId: string) => {
        const isOpening = !state.openItems[id];

        if (isOpening) {
            scrollSpy.locked.current = true;
            scrollSpy.manuallySetActiveElementId.current = id;
            scrollSpy.setActiveTocItem(id);
            setTimeout(() => scrollSpy.scrollToActiveTocItem(id), 0);

            if (!state.items[id]?.success && !state.items[id]?.isLoading) {
                fetchItem(id);
            }
        } else {
            if (scrollSpy.manuallySetActiveElementId.current === id) {
                scrollSpy.locked.current = false;
                scrollSpy.manuallySetActiveElementId.current = null;
            }
        }

        dispatch({
            type: ChecklistEventTypes.CLICK_ITEM,
            id,
            parent_id: parentId,
        });
    };

    const handleExpandAll = () => {
        const tocIds = Object.keys(checklistData.toc);

        if (allItemsExpanded) {
            dispatch({type: ChecklistEventTypes.COLLAPSE_ALL});
            return;
        }

        const allOpenItems: Record<string, boolean> = {};
        tocIds.forEach((id) => {
            allOpenItems[id] = true;
        });
        dispatch({
            type: ChecklistEventTypes.EXPAND_VISIBLE,
            openItems: allOpenItems,
        });

        if (!state.fetchedAll && !state.fetchingAll) {
            triggerFetchAll();
        }
    };

    const handleSearchChange = (term: string) => {
        if (state.searchTerm === term) return;

        if (term === "" && state.searchTerm !== "") {
            const hasActiveFilters =
                Object.keys(state.filter.statusFilterSelected).length > 0;
            if (hasActiveFilters) {
                pendingSelectiveExpansionRef.current = true;
            }
        }

        if (term !== "") {
            if (!state.fetchedAll && !state.fetchingAll) {
                pendingSelectiveExpansionRef.current = true;
                triggerFetchAll();
            } else if (state.fetchedAll) {
                pendingSelectiveExpansionRef.current = true;
            }
        }

        dispatch({
            type: ChecklistEventTypes.SET_SEARCH_TERM,
            searchTerm: term,
        });
    };

    const handleStatusToggle = (id: string, status: Status | string) => {
        if (state.filter.statusFilterSelected[id]?.includes(status as Status)) {
            dispatch({
                type: ChecklistEventTypes.REMOVE_STATUS,
                id,
                status: status as Status,
            });

            const updatedFilters = {
                ...state.filter.statusFilterSelected,
            };
            updatedFilters[id] = updatedFilters[id].filter((s) => s !== status);
            if (updatedFilters[id].length === 0) {
                delete updatedFilters[id];
            }

            const hasRemainingFilters = Object.keys(updatedFilters).length > 0;
            const hasSearchTerm = state.searchTerm.length > 0;

            if (hasRemainingFilters || hasSearchTerm) {
                pendingSelectiveExpansionRef.current = true;
            }
        } else {
            if (!state.fetchedAll && !state.fetchingAll) {
                pendingSelectiveExpansionRef.current = true;
                triggerFetchAll();
            } else if (state.fetchedAll) {
                pendingSelectiveExpansionRef.current = true;
            }
            dispatch({
                type: ChecklistEventTypes.SELECT_STATUS,
                id,
                status: status as Status,
            });
        }
    };

    const handlePatchCollectionName = async (name: string) => {
        const topicId = checklistData.metadata.topicId;
        const resourceId = checklistData.metadata.id;
        const cId = collectionData?.metadata?.collectionId;
        if (topicId && cId && resourceId) {
            await mutations.patchCollectionName({
                topicId,
                resourceId,
                collectionId: cId,
                name,
            });
        }
    };

    /**
     * `changedDimensions` are only the label ids the caller changed. The
     * PATCH endpoint replaces the collection's whole dimension set, so the
     * request must carry every view group's label: the changed ones merged
     * over the labels the view currently shows. Sending only the changed
     * ids deletes the other groups' rows server-side.
     */
    const handlePatchDimensions = async (changedDimensions: string[]) => {
        dispatch({type: ChecklistEventTypes.RESET_STATUS});
        dispatch({
            type: ChecklistEventTypes.SET_SEARCH_TERM,
            searchTerm: "",
        });

        scrollSpy.containerRef.current?.scrollTo({top: 0});
        scrollSpy.tocContainerRef.current?.scrollTo({top: 0});

        const topicId = checklistData.metadata.topicId;
        const resourceId = checklistData.metadata.id;
        const cId =
            collectionData?.metadata?.collectionId ??
            checklistData.defaultCollectionId;

        if (topicId && cId) {
            const labelsByGroupId = changedDimensions.reduce(
                (acc, current) => {
                    const labelObject = checklistData.objects?.[current];
                    if (
                        labelObject &&
                        (labelObject as {groupId?: string}).groupId
                    ) {
                        acc[(labelObject as {groupId: string}).groupId] =
                            current;
                    }
                    return acc;
                },
                {} as Record<string, string>
            );

            const labelsPayload = Object.keys(labelData).map((groupId) => ({
                collectionId: cId,
                groupId,
                labelId: labelsByGroupId[groupId] ?? labelData[groupId],
            }));

            await mutations.patchDimensions({
                topicId,
                resourceId,
                collectionId: cId,
                dimensions: labelsPayload.map((l) => l.labelId),
                labels: labelsPayload,
            });
        }
    };

    const handleConfirmCheckAll = () => {
        if (
            !modals.confirmCheckAllData ||
            modals.confirmCheckAllData.leafNodeIds.length === 0
        ) {
            return;
        }
        patchCheckItems(
            modals.confirmCheckAllData.leafNodeIds,
            modals.confirmCheckAllData.status
        );
        modals.closeModal();
    };

    const handleCheckItemStatusClick = ({
        id,
        status,
    }: {
        id: string;
        status: Status;
    }) => {
        handleStatusClickLogic({
            id,
            status,
            checklistData,
            collectionData,
            viewFilteredToc,
            openItems: state.openItems,
            openCreateFilling: modals.openCreateFilling,
            patchCheckItems,
            openConfirmCheckAll: modals.openConfirmCheckAll,
        });
    };

    const handleToggleDocumentSelected = (
        topicId: string,
        selected: boolean
    ) => {
        if (!collectionData?.metadata) {
            modals.openCreateFilling();
            return;
        }
        mutations.patchDocumentSelected({
            topicId: checklistData.metadata.topicId,
            resourceId: checklistData.metadata.id,
            collectionId: collectionData.metadata.collectionId,
            documentId: topicId,
            selected,
        });
    };

    const handleClearSearchAndFilters = () => {
        dispatch({
            type: ChecklistEventTypes.SET_SEARCH_TERM,
            searchTerm: "",
        });
        dispatch({type: ChecklistEventTypes.RESET_STATUS});
    };

    return {
        handleClickTocItem,
        handleClickChecklistItem,
        handleExpandAll,
        handleSearchChange,
        handleStatusToggle,
        handlePatchCollectionName,
        handlePatchDimensions,
        handleConfirmCheckAll,
        handleCheckItemStatusClick,
        handleToggleDocumentSelected,
        handleClearSearchAndFilters,
    };
};
